import React from "react";
import Image from "../../../components/AppImage";
import Icon from "../../../components/AppIcon";
import { useConfig } from "../../../context/ConfigContext";
import { formatNumber } from "../../../components/utils/format";
import { roundTo } from "../../../components/utils/mathUtils";

const HeroSection = () => {
  const config = useConfig();
  const {
    COMPANY_NAME,
    ENERGIA_TOTAL,
    PLANTAS_SOLARES,
    SATISFACTION_CLIENT,
    CO2_TOTAL,
    CARBON_TOTAL,
    TOTAL_ARBOLES,
    INICIO_DESDE,
  } = config;
  return (
    <section className="relative bg-gradient-to-br from-warm-white via-background to-muted min-h-screen flex items-center overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-32 h-32 rounded-full bg-primary animate-pulse"></div>
        <div className="absolute bottom-32 right-16 w-24 h-24 rounded-full bg-secondary animate-pulse animation-delay-400"></div>
        <div className="absolute top-1/2 left-1/4 w-16 h-16 rounded-full bg-accent animate-pulse animation-delay-200"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                  <Icon name="Sun" size={24} color="white" />
                </div>
                <span className="text-primary font-semibold text-lg">
                  Acerca de {COMPANY_NAME}
                </span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-text-primary leading-tight">
                Pioneros en la
                <span className="solar-text-gradient block">
                  Revolución Solar
                </span>
                de Guatemala
              </h1>

              <p className="text-xl text-text-secondary leading-relaxed max-w-2xl">
                Desde {INICIO_DESDE}, hemos estado transformando el panorama
                energético local, combinando tecnología de vanguardia con un
                profundo compromiso hacia nuestro país y su futuro sostenible.
              </p>
            </div>

            {/* Key Stats */}
            <div className="grid grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-primary">
                  {PLANTAS_SOLARES}+
                </div>
                <div className="text-sm text-text-secondary">
                  Proyectos Completados
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-secondary">
                  {formatNumber(ENERGIA_TOTAL)}Wh
                </div>
                <div className="text-sm text-text-secondary">
                  Capacidad Instalada
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-accent">
                  {SATISFACTION_CLIENT}%
                </div>
                <div className="text-sm text-text-secondary">
                  Satisfacción Cliente
                </div>
              </div>
            </div>

            {/* Mission Statement */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 solar-shadow-card">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name="Target" size={20} color="white" />
                </div>
                <div>
                  <h3 className="font-semibold text-text-primary mb-2">
                    Nuestra Misión
                  </h3>
                  <p className="text-text-secondary">
                    Hacer de Guatemala un país energéticamente independiente,
                    preservando su belleza natural mientras empoderamos a cada
                    familia y empresa con energía limpia y asequible.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Hero Image */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden solar-shadow-elevated">
              <Image
                src="https://images.unsplash.com/photo-1509391366360-2e959784a276?w=600&h=700&fit=crop"
                alt="Instalación solar en Guatemala con paisaje montañoso"
                className="w-full h-[600px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>

              {/* Floating Achievement Card */}
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-xl p-4 solar-shadow-card">
                <div className="text-md font-bold text-text-secondary mb-3 text-center">
                  Impacto Ambiental
                </div>
                <div className="flex items-center justify-between space-x-4">
                  <div className="flex items-center space-x-3">
                    <div>
                      <div className="text-lg font-bold text-blue-600">
                        {roundTo(formatNumber(CO2_TOTAL))} (t)
                      </div>
                      <div className="text-xs text-text-secondary">
                        CO₂ evitado
                      </div>
                    </div>
                    <div className="w-11 h-11 bg-blue-600 rounded-full flex items-center justify-center">
                      <Icon name="Factory" size={24} color="white" />
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div>
                      <div className="text-lg font-bold text-orange-600">
                        {roundTo(formatNumber(CARBON_TOTAL))} (t)
                      </div>
                      <div className="text-xs text-text-secondary">
                        Ahorro de carbón
                      </div>
                    </div>
                    <div className="w-11 h-11 bg-orange-600 rounded-full flex items-center justify-center">
                      <Icon name="MountainSnow" size={24} color="white" />
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div>
                      <div className="text-lg font-bold text-secondary">
                        {formatNumber(TOTAL_ARBOLES)} Árboles
                      </div>
                      <div className="text-xs text-text-secondary">
                        Equivalentes plantados
                      </div>
                    </div>
                    <div className="w-11 h-11 bg-secondary rounded-full flex items-center justify-center">
                      <Icon name="Trees" size={24} color="white" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Decorative Elements*/}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary/20 rounded-full blur-xl"></div>
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-secondary/20 rounded-full blur-xl"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
